import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../../utils/auth";
import { Building2, Receipt, ReceiptText, User } from "lucide-react";
import { toast } from "react-toastify";
import { getCardBrandIcon, parseDate } from "../../shared/Utils";
import { API_BASE, TOASTPROPS } from "../../shared/Constants";
import { PaymentContext } from "../../contexts/PaymentContext";
import { OrganizationSummary } from "../../shared/Interfaces";
import { hasPermission, OrganizationPermission } from "../../shared/organizationPermissions";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { cn } from "../../lib/utils";
import { reportHandledError, reportApiError } from "../../utils/reportError";

export interface BasicCardInfo {
  card_brand: string;
  card_expiration: string;
  card_last4: string;
}

export interface Charge {
  id: string;
  value: number;
  date: string;
  status: string;
  payment_method: string | BasicCardInfo;
  receipt_url: string | null;
}

export interface ChargesResponse {
  charges: Charge[];
  has_more: boolean;
  last_charge: string | null;
  last_intent: string | null;
}

export default function Receipts() {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [charges, setCharges] = useState<ChargesResponse>();
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(false);
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();

  const {
    billingScope,
    activeOrganizationId,
    setBillingScope,
    setActiveOrganizationId,
  } = useContext(PaymentContext);

  const buildBillingUrl = (path: string, params: Record<string, string> = {}) => {
    const search = new URLSearchParams({
      ...(billingScope === "organization" && activeOrganizationId
        ? { organization_id: activeOrganizationId }
        : {}),
      ...params,
    });
    const query = search.toString();
    return `${API_BASE}/api/billing${path}${query ? `?${query}` : ""}`;
  };

  const currentUserId = useMemo(() => {
    if (!user?.sub) return "";
    const parts = user.sub.split("|");
    return parts[parts.length - 1] || user.sub;
  }, [user]);

  const getPermissionsForOrg = useCallback(
    (orgId?: string | null): string[] => {
      if (!orgId) return [];
      const org = organizations.find((item) => item.organization_id === orgId);
      if (!org) return [];
      const membership = org.members?.find((member) => member.user_id === currentUserId);
      return membership?.permissions ?? [];
    },
    [organizations, currentUserId]
  );

  const accessibleOrganizations = useMemo(() => {
    return organizations.filter((org) => {
      const membership = org.members?.find((member) => member.user_id === currentUserId);
      if (!membership) return false;
      return hasPermission(
        membership.permissions ?? [],
        OrganizationPermission.BILLING_VIEW_RECEIPTS
      );
    });
  }, [organizations, currentUserId]);

  const currentOrgPermissions = useMemo(
    () => getPermissionsForOrg(activeOrganizationId),
    [getPermissionsForOrg, activeOrganizationId]
  );
  const organizationReceiptsLocked =
    billingScope === "organization" && activeOrganizationId
      ? !hasPermission(currentOrgPermissions, OrganizationPermission.BILLING_VIEW_RECEIPTS)
      : false;

  useEffect(() => {
    if (billingScope !== "organization") return;
    if (!accessibleOrganizations.length) {
      setBillingScope("personal");
      setActiveOrganizationId("");
      return;
    }
    if (!accessibleOrganizations.some((org) => org.organization_id === activeOrganizationId)) {
      setActiveOrganizationId(accessibleOrganizations[0].organization_id);
    }
  }, [
    billingScope,
    accessibleOrganizations,
    activeOrganizationId,
    setBillingScope,
    setActiveOrganizationId,
  ]);

  const handleScopeChange = (value: string) => {
    if (value === "personal") {
      setBillingScope("personal");
      setActiveOrganizationId("");
    } else {
      setBillingScope("organization");
      setActiveOrganizationId(value);
    }
  };

  const scopeSelectValue =
    billingScope === "personal" ? "personal" : activeOrganizationId || "";
  const hasAccessibleOrganizations = accessibleOrganizations.length > 0;
  const hasOrganizations = organizations.length > 0;

  const fetchCharges = useCallback(
    async (last_charge: string | null = null, last_intent: string | null = null) => {
      if (billingScope === "organization" && !activeOrganizationId) {
        setCharges(undefined);
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      if (billingScope === "organization") {
        const permissions = getPermissionsForOrg(activeOrganizationId);
        if (!hasPermission(permissions, OrganizationPermission.BILLING_VIEW_RECEIPTS)) {
          setCharges(undefined);
          setLoading(false);
          setLoadingMore(false);
          return;
        }
      }

      if (last_charge || last_intent) setLoadingMore(true);
      else setLoading(true);

      try {
        const params: Record<string, string> = {};
        if (last_charge) params.last_charge = last_charge;
        if (last_intent) params.last_intent = last_intent;
        const url = buildBillingUrl("/get-charges", params);
        const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!access_token) return;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Auth ${access_token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setCharges((prev) => {
            const newCharges: Charge[] =
              prev && last_charge ? [...prev.charges, ...data.charges] : data.charges;
            const dedupedCharges: Charge[] = Array.from(
              new Map(newCharges.map((charge: Charge) => [charge.id, charge])).values()
            );
            return {
              charges: dedupedCharges,
              has_more: data.has_more,
              last_charge: data.last_charge,
              last_intent: data.last_intent,
            };
          });
        } else {
          reportApiError(response, { feature: "receipts.fetch", method: "GET" });
          toast.error("Erro ao recuperar cobranças", TOASTPROPS);
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "receipts.fetch" });
        toast.error("Erro ao recuperar cobranças", TOASTPROPS);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [billingScope, activeOrganizationId, getAccessTokenSilently, getPermissionsForOrg]
  );

  const parseValue = (value: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case "succeeded":
      case "paid":
      case "pago":
        return "Pago";
      case "pending":
      case "pendente":
        return "Pendente";
      case "failed":
      case "falhou":
        return "Falhou";
      case "canceled":
      case "cancelado":
        return "Cancelado";
      default:
        return status;
    }
  };

  const statusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "succeeded":
      case "paid":
      case "pago":
        return "bg-success/10 text-success";
      case "pending":
      case "pendente":
        return "bg-warning/10 text-warning";
      case "failed":
      case "canceled":
      case "falhou":
      case "cancelado":
        return "bg-destructive/10 text-destructive";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  useEffect(() => {
    const fetchOrganizations = async () => {
      setLoadingOrganizations(true);
      try {
        const access_token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!access_token) return;
        const response = await fetch(`${API_BASE}/api/organizations/list`, {
          method: "GET",
          headers: { Authorization: `Auth ${access_token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setOrganizations(data.organizations ?? []);
        } else {
          reportApiError(response, { feature: "receipts.organizations", method: "GET" });
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "receipts.organizations" });
      } finally {
        setLoadingOrganizations(false);
      }
    };
    fetchOrganizations();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (loadingOrganizations) return;
    fetchCharges();
  }, [fetchCharges, loadingOrganizations]);

  return (
    <>
      <div className="mb-3 flex w-full flex-col items-start gap-3 sm:w-[360px]">
        <span className="text-[13px] font-medium text-foreground">
          Visualizar comprovantes de:
        </span>
        {loadingOrganizations ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <Select value={scopeSelectValue || "personal"} onValueChange={handleScopeChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="personal">
                <span className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Minha conta pessoal
                </span>
              </SelectItem>
              {accessibleOrganizations.map((org) => (
                <SelectItem key={org.organization_id} value={org.organization_id}>
                  <span className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {org.name || org.organization_id}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {organizationReceiptsLocked ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-6 text-center text-[13px] font-semibold text-destructive">
          Você não tem permissão para visualizar comprovantes desta organização.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-[800px] table-fixed text-sm">
              <colgroup>{[15, 19, 14, 27, 25].map((width, i) => <col key={i} style={{ width: width + "%" }} />)}</colgroup>
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3">Valor</th>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Forma de Pagamento</th>
                  <th className="px-4 py-3">Comprovante</th>
                </tr>
              </thead>
              <tbody>
                {loading &&
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index} className="h-12 border-b border-border/60">
                      <td className="px-4 py-3"><Skeleton className="max-w-full h-4 w-24" /></td>
                      <td className="px-4 py-3"><Skeleton className="max-w-full h-4 w-28" /></td>
                      <td className="px-4 py-3"><Skeleton className="max-w-full h-4 w-20" /></td>
                      <td className="px-4 py-3"><Skeleton className="max-w-full h-4 w-40" /></td>
                      <td className="px-4 py-3"><Skeleton className="max-w-full h-4 w-32" /></td>
                    </tr>
                  ))}

                {!loading &&
                  charges?.charges.map((charge, key) => (
                    <tr key={key} className="border-b border-border/60 transition-colors hover:bg-muted/30">
                      <td className="px-4 py-3 font-semibold text-success">
                        {parseValue(charge.value)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{parseDate(charge.date)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
                            statusClass(charge.status)
                          )}
                        >
                          {getStatusText(charge.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {typeof charge.payment_method === "string" ? (
                          charge.payment_method.toLowerCase() === "boleto" ? (
                            <span className="inline-flex items-center gap-2 text-[13px]">
                              <ReceiptText className="h-3.5 w-3.5 text-muted-foreground" />
                              Boleto Bancário
                            </span>
                          ) : (
                            <span className="text-[13px] capitalize text-foreground/90">
                              {charge.payment_method}
                            </span>
                          )
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            <img
                              src={getCardBrandIcon(charge.payment_method.card_brand)}
                              alt="Card"
                              className="h-6 w-7 rounded border border-border bg-muted p-0.5"
                            />
                            <span className="font-mono text-[12px] tracking-wide text-foreground">
                              **** {charge.payment_method.card_last4}
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {charge.receipt_url ? (
                          <a
                            href={charge.receipt_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
                          >
                            <Receipt className="h-3.5 w-3.5" />
                            Ver comprovante
                          </a>
                        ) : (
                          <span className="text-[12px] italic text-muted-foreground">
                            Indisponível
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {!loading && charges?.charges.length === 0 && (
              <div className="sticky left-0 flex h-60 w-full items-center justify-center px-4 py-8 text-center">
<div className="mx-auto flex max-w-sm flex-col items-center gap-2">
                        <span className="flex h-12 w-12 items-center justify-center rounded-md bg-muted text-muted-foreground">
                          <Receipt className="h-5 w-5" />
                        </span>
                        <p className="text-[14px] font-semibold text-foreground">
                          Nenhuma transação encontrada
                        </p>
                        <p className="text-[13px] text-muted-foreground">
                          Suas transações aparecerão aqui após o primeiro pagamento.
                        </p>
                      </div>
              </div>
            )}
          </div>

          {!loading && charges?.has_more && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => fetchCharges(charges.last_charge, charges.last_intent)}
                disabled={loadingMore}
              >
                {loadingMore ? "Carregando..." : "Carregar mais"}
              </Button>
            </div>
          )}
        </>
      )}
      {!loadingOrganizations && !hasOrganizations && (
        <p className="mt-3 text-[12px] text-muted-foreground">
          💡 Dica: Crie uma organização para compartilhar comprovantes com sua equipe.
        </p>
      )}
      {!loadingOrganizations && hasOrganizations && !hasAccessibleOrganizations && (
        <p className="mt-3 text-[12px] text-warning">
          ⚠️ Você não possui permissão para visualizar comprovantes em nenhuma organização.
        </p>
      )}

    </>
  );
}
